/* DAILE v2 (dv) — page transitions, the non-critical half (round 6).
 *
 * The critical half is inline in layout/theme.liquid (it must run before the
 * first paint): opt-in fade for browsers without cross-document View
 * Transitions and the view-transition-name of the header / announcement strip
 * during a transition. Styles: assets/dv-page-transition.css.
 *
 * This file:
 *   1. Loading line — a 2px gold line at the top while the next page loads.
 *      Starts on a navigation to another document of the shop (Navigation API
 *      "navigate" event; older browsers: link clicks and form submits that
 *      nobody prevented), appears only if the page is still here after
 *      150 ms (fast navigations show nothing), fades out by itself after 12 s
 *      (a download, a 204, a cancelled navigation) and is cleared when the
 *      page comes back from the back/forward cache. Never for #anchors, new
 *      tabs, downloads, mailto:/tel:, other sites, or links handled by JS
 *      (cart drawer, dialogs: they call preventDefault).
 *   2. Continuous marquee — the announcement strip resumes at the position it
 *      had on the previous page instead of restarting, so the strip reads as
 *      one piece across navigations (sessionStorage, 20 s window). It only
 *      touches the WAAPI animation dv-announcement.js creates on
 *      [data-dv-ann-track]; nothing happens if that animation is absent.
 */
(() => {
  'use strict';

  const doc = document;
  if (window.Shopify && window.Shopify.designMode) return;

  /* 1. Loading line ------------------------------------------------------- */

  const SHOW_AFTER = 150;
  const GIVE_UP_AFTER = 12000;
  let line = null;
  let showTimer = 0;
  let giveUpTimer = 0;

  const ensureLine = () => {
    if (!line) {
      line = doc.createElement('div');
      line.className = 'dv-pt-progress';
      line.setAttribute('aria-hidden', 'true');
      doc.body.appendChild(line);
    }
    return line;
  };

  const stop = () => {
    window.clearTimeout(showTimer);
    window.clearTimeout(giveUpTimer);
    showTimer = 0;
    giveUpTimer = 0;
    if (line) line.classList.remove('is-active');
  };

  const start = () => {
    stop();
    showTimer = window.setTimeout(() => {
      const el = ensureLine();
      el.classList.remove('is-active');
      void el.offsetWidth; // restart the CSS animation
      el.classList.add('is-active');
    }, SHOW_AFTER);
    giveUpTimer = window.setTimeout(stop, GIVE_UP_AFTER);
  };

  const sameShop = (url) => {
    try {
      const target = new URL(url, doc.baseURI);
      if (target.origin !== window.location.origin) return false;
      // Same page, only the #fragment differs: no new document.
      const here = window.location;
      if (target.hash && target.pathname === here.pathname && target.search === here.search) return false;
      return true;
    } catch (error) {
      return false;
    }
  };

  if (window.navigation && typeof window.navigation.addEventListener === 'function') {
    window.navigation.addEventListener('navigate', (event) => {
      if (event.destination.sameDocument || event.hashChange || event.downloadRequest) return;
      if (!sameShop(event.destination.url)) return;
      start();
    });
  } else {
    // Bubble phase on window: runs after the handlers of the link itself,
    // the document and the dialogs, so a prevented click is seen as such.
    window.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!link || link.hasAttribute('download')) return;
      const target = (link.getAttribute('target') || '').toLowerCase();
      if (target && target !== '_self') return;
      if (!/^https?:/.test(link.protocol)) return;
      if (!sameShop(link.href)) return;
      start();
    });
    window.addEventListener('submit', (event) => {
      const form = event.target;
      if (event.defaultPrevented || !(form instanceof HTMLFormElement)) return;
      const target = (form.getAttribute('target') || '').toLowerCase();
      if (target && target !== '_self') return;
      if (!sameShop(form.action || window.location.href)) return;
      start();
    });
  }

  // Back/forward cache: the page returns exactly as it was left.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) stop();
  });

  /* 2. Continuous marquee --------------------------------------------------- */

  const KEY = 'dv-pt-marquee';
  const MAX_AGE = 20000;

  const marquee = () => {
    const track = doc.querySelector('[data-dv-announcement] [data-dv-ann-track]');
    if (!track || typeof track.getAnimations !== 'function') return null;
    return track.getAnimations().find((a) => a.effect && a.effect.getTiming().iterations === Infinity) || null;
  };

  const progressOf = (animation) => {
    const duration = Number(animation.effect.getTiming().duration);
    if (!(duration > 0) || animation.currentTime == null) return null;
    return { p: (Number(animation.currentTime) % duration) / duration, d: duration };
  };

  // pageswap (Chromium, Safari) fires right before the old page is captured;
  // pagehide covers the other browsers and runs later, so it simply refreshes
  // the same record.
  const remember = () => {
    const animation = marquee();
    const state = animation && progressOf(animation);
    if (!state) return;
    try {
      window.sessionStorage.setItem(KEY, JSON.stringify({ p: state.p, d: state.d, t: Date.now() }));
    } catch (error) {
      /* storage blocked: the strip simply restarts */
    }
  };
  window.addEventListener('pageswap', remember);
  window.addEventListener('pagehide', remember);

  const readSaved = () => {
    let saved = null;
    try {
      saved = JSON.parse(window.sessionStorage.getItem(KEY) || 'null');
      window.sessionStorage.removeItem(KEY);
    } catch (error) {
      saved = null;
    }
    return saved && typeof saved.p === 'number' && Date.now() - saved.t < MAX_AGE ? saved : null;
  };

  // The inline script (pagereveal) may have pre-positioned the track with an
  // inline transform before the first paint; the animation takes over here.
  const resume = (saved) => {
    const track = doc.querySelector('[data-dv-announcement] [data-dv-ann-track]');
    let frames = 0;
    const tick = () => {
      const animation = marquee();
      if (!animation) {
        if (++frames < 90) window.requestAnimationFrame(tick);
        else if (track) track.style.transform = '';
        return;
      }
      const duration = Number(animation.effect.getTiming().duration);
      if (duration > 0) {
        // Where the strip would be now had it kept running.
        const progress = (saved.p + (Date.now() - saved.t) / saved.d) % 1;
        animation.currentTime = progress * duration;
      }
      if (track) track.style.transform = '';
    };
    tick();
  };

  // The inline script has already taken the state out of sessionStorage
  // (it needs it before the first paint).
  const early = window.DVPageTransition && window.DVPageTransition.marquee;
  const saved = early && Date.now() - early.t < MAX_AGE ? early : readSaved();
  if (saved) {
    // dv-announcement.js starts the marquee on DOMContentLoaded, after this
    // deferred file has run.
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', () => window.requestAnimationFrame(() => resume(saved)), { once: true });
    else window.requestAnimationFrame(() => resume(saved));
  }

  // Back to a page kept in the back/forward cache: continue from the page
  // we are coming back from.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    const state = readSaved();
    if (state) resume(state);
  });
})();
